import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  End,
  Home,
  PageDown,
  PageUp,
} from '@fluentui/keyboard-keys'
import { XIcon } from 'lucide-react'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import { useOutsideClick } from '@/lib/ui/outside-click.hook'
import { cn } from '@/lib/ui/style-helpers'

import {
  Select as SelectBase,
  SelectContent,
  SelectGroup,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/legacy-design-system/select/select'
import { Button } from '@/components/lemmala-design-system/button/button'
import { Textarea } from '@/components/lemmala-design-system/textarea/textarea'
import { MenuItem } from '@/components/shared/submit-select/types'
import {
  type TriggerAppearance,
  triggerAppearanceProps,
} from '@/components/shared/submit-select/variants'

import { Item } from './item'

export type SubmitSelectProps<T extends number | string> = {
  title?: string
  value?: T
  placeholder?: string
  menuItems: MenuItem<T>[]
  onOpenChange?: (isOpen: boolean) => void
  onSave?: (value: T, comment: string) => void
  onSendComment?: (comment: string) => void
  selectedValueRenderer?: (value: T) => ReactNode
  onSelectChange?: (value: T) => void
  shouldCloseSelect?: (value: T) => boolean
  showButtonsRow?: boolean
  showCommentBox?: boolean
  triggerAppearance?: TriggerAppearance
}

export const SubmitSelect = <T extends number | string>({
  menuItems,
  title,
  value,
  onOpenChange,
  onSave,
  onSendComment,
  triggerAppearance = 'headless',
  placeholder,
  selectedValueRenderer,
  onSelectChange,
  shouldCloseSelect,
  showCommentBox = true,
  showButtonsRow = true,
}: SubmitSelectProps<T>) => {
  const appearance = triggerAppearanceProps[triggerAppearance]
  const [isOpen, setIsOpen] = useState(false)
  const [intendedValue, setIntendedValue] = useState<T | null>(null)
  const [commentsMap, setCommentsMap] = useState<Record<string, string>>({})
  const ref = useRef<HTMLDivElement>(null)
  useOutsideClick(ref, () => setIsOpen(false))

  useEffect(() => {
    if (onOpenChange) onOpenChange(isOpen)
  }, [isOpen, onOpenChange])

  const handleCommentChange = useCallback(
    (newComment: string) => {
      if (intendedValue !== null) {
        setCommentsMap((prev) => ({
          ...prev,
          [intendedValue.toString()]: newComment,
        }))
        if (onSendComment) onSendComment(newComment)
      }
    },
    [onSendComment, intendedValue],
  )

  const reset = () => {
    setIsOpen(false)
    setIntendedValue(null)
    setCommentsMap({})
  }

  return (
    <SelectBase
      onValueChange={(newValue) => {
        // The internal select supports only strings but we want native support for numbers. This is why
        // we convert numbers to strings and here we need to convert strings back to numbers
        // in case the original input was numeric
        const convertedNewValue = (
          typeof value === 'number' ? Number(newValue) : newValue.toString()
        ) as T

        setIntendedValue(convertedNewValue)
        onSelectChange?.(convertedNewValue)
        if (
          shouldCloseSelect?.(convertedNewValue) ||
          (onSelectChange && convertedNewValue && !onSave)
        ) {
          setIsOpen(false)
        }
      }}
      open={isOpen}
      value={value && value.toString()}
    >
      <SelectTrigger
        variant={appearance.variant}
        inputSize={appearance.inputSize}
        onClick={(e) => {
          e.stopPropagation()
          setIsOpen(!isOpen)
          // This hack is for making sure a click-outside is registered.
          // For some reason without it other open Select components will not close on a click outside even when the click was in a different select component
          document.dispatchEvent(new Event('mousedown'))
          if (!isOpen) {
            setIntendedValue(null)
            setCommentsMap({})
          }
        }}
        className='gap-1'
      >
        <div>
          {selectedValueRenderer && value ? (
            selectedValueRenderer(value)
          ) : (
            <SelectValue placeholder={<div className='w-full text-left'>{placeholder}</div>} />
          )}
        </div>
      </SelectTrigger>
      <SelectContent
        onClick={(e) => e.stopPropagation()}
        ref={ref}
        className={cn('z-max p-2', appearance.contentClassName)}
      >
        <SelectGroup>
          {title && (
            <>
              <SelectLabel className='flex items-center justify-between gap-4 pt-0 text-main-foreground-primary text-heading-xxsmall'>
                {title}
                <Button variant='ghost' size='sm' onClick={reset}>
                  <XIcon size={16} className='text-main-foreground-primary' />
                </Button>
              </SelectLabel>
              <SelectSeparator />
            </>
          )}

          <div className='max-h-112 space-y-0.5 overflow-y-auto'>
            {menuItems.map((item, index) => {
              if (item.type === 'menuSelectItem') {
                item.isPotentialSelection = item.value === intendedValue
              }

              return (
                <div key={index}>
                  <Item item={item} />
                  <InputComment
                    warningMessage={
                      item.type === 'menuSelectItem' ? item.warningMessage : undefined
                    }
                    warningMessageRenderer={
                      item.type === 'menuSelectItem' ? item.warningMessageRenderer : undefined
                    }
                    isOpen={
                      !!(
                        intendedValue &&
                        item.type === 'menuSelectItem' &&
                        item.value === intendedValue
                      )
                    }
                    comment={intendedValue ? commentsMap[intendedValue.toString()] || '' : ''}
                    setComment={handleCommentChange}
                    showCommentBox={showCommentBox}
                  />
                </div>
              )
            })}
          </div>
        </SelectGroup>
        {intendedValue && showButtonsRow && (
          <div className='flex justify-end gap-2 pt-4'>
            <Button variant='ghost' size='sm' onClick={reset}>
              Cancel
            </Button>
            <Button
              size='sm'
              disabled={!intendedValue}
              onClick={() => {
                if (onSave && intendedValue) {
                  onSave(
                    intendedValue,
                    showCommentBox ? commentsMap[intendedValue.toString()] || '' : '',
                  )
                  reset()
                }
              }}
            >
              Save
            </Button>
          </div>
        )}
      </SelectContent>
    </SelectBase>
  )
}

type InputCommentProps = {
  isOpen: boolean
  comment: string
  setComment: (comment: string) => void
  warningMessage?: ReactNode
  warningMessageRenderer?: (value: ReactNode) => ReactNode
  showCommentBox?: boolean
}

const NAVIGATION_KEYS = new Set([
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  PageUp,
  PageDown,
  Home,
  End,
])

const InputComment = ({
  isOpen,
  comment,
  warningMessage,
  setComment,
  warningMessageRenderer,
  showCommentBox = true,
}: InputCommentProps) => {
  if (!isOpen) return null

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (NAVIGATION_KEYS.has(e.key)) {
      e.stopPropagation()
    }
  }

  return (
    <>
      <div className='w-full'>
        {warningMessage && warningMessageRenderer
          ? warningMessageRenderer(warningMessage)
          : warningMessage}
      </div>
      {showCommentBox && (
        <Textarea
          className='mt-2'
          placeholder='Document context on this change in a comment.'
          onChange={(e) => setComment(e.target.value)}
          value={comment}
          onKeyDown={handleKeyDown}
        />
      )}
    </>
  )
}
